import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Loader2, Save, TriangleAlert } from "lucide-react";
import {
  ADS_FILE_NAMES,
  type AdsFileName,
} from "../../../../../shared/index";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";
import { cn } from "@/lib/utils";

function describeError(e: unknown): string {
  return e instanceof ApiError ? e.message : "Something went wrong";
}

export default function AdsPage() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<AdsFileName>("ads.txt");

  // Local editor buffer plus the last-loaded value, so we can detect edits.
  const [draft, setDraft] = useState("");
  const [baseline, setBaseline] = useState<string | null>(null);

  const queryKey = ["ads", file] as const;
  const fileQuery = useQuery({
    queryKey,
    queryFn: () => api.ads.getFile(file),
  });

  // Reset the editor whenever a new file loads.
  useEffect(() => {
    if (fileQuery.data) {
      setDraft(fileQuery.data.content);
      setBaseline(fileQuery.data.content);
    }
  }, [fileQuery.data]);

  const saveMutation = useMutation({
    mutationFn: () => api.ads.updateFile({ file, content: draft }),
    onSuccess: (data) => {
      setBaseline(draft);
      queryClient.setQueryData(queryKey, {
        env: data.env,
        file,
        blobName: data.blobName,
        content: draft,
        exists: true,
      });
      toast.success(`Saved ${file} (${data.env})`);
    },
    onError: (e) => toast.error(describeError(e)),
  });

  // The environment is fixed per deployment; the server tells us which one.
  const env = fileQuery.data?.env;
  const blobName = fileQuery.data?.blobName;
  const dirty = baseline !== null && draft !== baseline;
  const loading = fileQuery.isLoading;

  return (
    <div className="space-y-6">
      <div className="space-y-1">
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="text-2xl font-bold">Ads</h1>
          {env && (
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide",
                env === "prod"
                  ? "bg-red-500/15 text-red-600 dark:text-red-400"
                  : "bg-amber-500/15 text-amber-600 dark:text-amber-400"
              )}
            >
              {env}
            </span>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Edit <code className="rounded bg-muted px-1">ads.txt</code> and{" "}
          <code className="rounded bg-muted px-1">app-ads.txt</code> for this
          deployment&apos;s environment. Saving overwrites the file in frontend
          storage.
        </p>
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          File
        </p>
        <div className="inline-flex rounded-lg border p-1">
          {ADS_FILE_NAMES.map((f) => (
            <button
              key={f}
              onClick={() => setFile(f)}
              className={cn(
                "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                file === f
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {f}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            {blobName ? (
              <>
                Editing{" "}
                <code className="rounded bg-muted px-1">$web/{blobName}</code>
              </>
            ) : (
              <>Loading…</>
            )}
          </p>
          {dirty && (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600 dark:text-amber-400">
              <TriangleAlert className="h-3.5 w-3.5" />
              Unsaved changes
            </span>
          )}
          {!dirty && baseline !== null && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <CheckCircle2 className="h-3.5 w-3.5" />
              {fileQuery.data?.exists ? "Saved" : "New file"}
            </span>
          )}
        </div>

        {fileQuery.isError ? (
          <div className="flex items-start gap-2 rounded-lg border border-destructive/50 bg-destructive/10 px-4 py-3 text-sm text-destructive">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>Failed to load file: {describeError(fileQuery.error)}</span>
          </div>
        ) : (
          <div className="relative">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              disabled={loading}
              spellCheck={false}
              placeholder={loading ? "" : "# This file is empty"}
              className="h-[420px] w-full resize-y rounded-lg border bg-card px-3 py-2 font-mono text-xs leading-relaxed text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-60"
            />
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex items-center gap-3">
        <Button
          onClick={() => saveMutation.mutate()}
          disabled={loading || !dirty || saveMutation.isPending}
        >
          {saveMutation.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Save className="h-4 w-4" />
          )}
          Save {file}
        </Button>
        {dirty && (
          <Button
            variant="outline"
            onClick={() => baseline !== null && setDraft(baseline)}
            disabled={saveMutation.isPending}
          >
            Discard changes
          </Button>
        )}
      </div>
    </div>
  );
}
